import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import axiosInstance from '@core/utils/axiosInstance';
import type { ChasubleValueFields, StoleValueFields } from './valueFields';

export interface PastCombination extends ChasubleValueFields, StoleValueFields {
  /** Order-item id (one row per past line). */
  id: number;
  customerName: string;
  orderCreatedAt: string | null;
}

export interface PastCombinationPage {
  items: PastCombination[];
  total: number;
  offset: number;
  limit: number;
}

export const PAST_COMBINATION_PAGE_SIZE = 10;

export const pastCombinationKeys = {
  all: ['pastCombinations'] as const,
  byDesign: (designId: number) => ['pastCombinations', designId] as const,
  page: (designId: number) => ['pastCombinations', designId, 'page'] as const,
  availability: (designId: number) =>
    ['pastCombinations', designId, 'availability'] as const,
};

export async function getPastCombinationsPage(
  designId: number,
  params: { offset: number; limit: number }
): Promise<PastCombinationPage> {
  const { data } = await axiosInstance.get<PastCombinationPage>(
    `/design/${designId}/past-combinations`,
    { params }
  );
  return data;
}

/** First page only — enough to know whether past combinations exist. */
export const pastCombinationsAvailabilityQuery = (designId: number) =>
  queryOptions({
    queryKey: pastCombinationKeys.availability(designId),
    queryFn: () =>
      getPastCombinationsPage(designId, {
        offset: 0,
        limit: 1,
      }),
  });

export const pastCombinationsInfiniteQuery = (designId: number) =>
  infiniteQueryOptions({
    queryKey: pastCombinationKeys.page(designId),
    queryFn: ({ pageParam }) =>
      getPastCombinationsPage(designId, {
        offset: pageParam,
        limit: PAST_COMBINATION_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: PastCombinationPage,
      allPages: PastCombinationPage[]
    ) => {
      const loaded = allPages.reduce(
        (count, page) => count + page.items.length,
        0
      );
      return lastPage.items.length > 0 && loaded < lastPage.total
        ? loaded
        : undefined;
    },
  });
